import React from "react";
import type { NegotiationBalancePreview } from "../../services/negotiationBalance";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import {
  balanceShortfall,
  ceilReais,
  DISABLED_COPY,
  RESERVATION_COPY,
  RESTRICTED_COPY,
} from "./negotiationBalanceFormat";
import "./NegotiationBalance.css";

export type NegotiationShortfallProps = {
  /** The org-tax-preview answer for this exact bid; Payments still decides. */
  preview: NegotiationBalancePreview;
  /** Opens the recharge QR, prefilled with the missing whole reais; absent hides the shortcut. */
  onRecharge?: (amountBRL?: number) => void;
  /**
   * The preview is the coarse estimate of a turbo bid without its own quote:
   * the missing amount is a lower bound and the copy says so.
   */
  atLeast?: boolean;
};

/**
 * What the Saldo de Negociações says about one bid, next to the bid button:
 * the reservation rule always, and, when the balance cannot admit the bid,
 * how much is missing and a shortcut to recharge exactly that.
 *
 * The previous bid the company still holds on the same post is listed apart, because it is the most common reason
 * a raise seems to need twice the money.
 * @story ancestor #negotiationHomeContainer
 * @story css ../../routes/pages/negotiate.css
 * @story args { preview: { rail: "prepaid", accountState: "active", prepaidRailEnabled: true, availableCents: 4000, reservedCents: 6000, reservedOnPostCents: 0, pendingTopupCents: 0, requiredCents: 10000, missingCents: 6000 } }
 * @story state Covered { preview: { rail: "prepaid", accountState: "active", prepaidRailEnabled: true, availableCents: 40000, reservedCents: 6000, reservedOnPostCents: 0, pendingTopupCents: 0, requiredCents: 10000, missingCents: 0 } }
 * @story state HeldByAutobuy { preview: { rail: "prepaid", accountState: "active", prepaidRailEnabled: true, availableCents: 4000, reservedCents: 6000, reservedOnPostCents: 5000, pendingTopupCents: 0, requiredCents: 10000, missingCents: 6000 } }
 * @story state Restricted { preview: { rail: "prepaid", accountState: "restricted", prepaidRailEnabled: true, availableCents: 0, reservedCents: 0, reservedOnPostCents: 0, pendingTopupCents: 0, requiredCents: 10000, missingCents: 10000 } }
 * @story state AtLeast { atLeast: true, preview: { rail: "prepaid", accountState: "active", prepaidRailEnabled: true, availableCents: 4000, reservedCents: 6000, reservedOnPostCents: 0, pendingTopupCents: 0, requiredCents: 5000, missingCents: 1000 } }
 * @story state RailDisabled { preview: { rail: "prepaid", accountState: "active", prepaidRailEnabled: false, availableCents: 40000, reservedCents: 0, reservedOnPostCents: 0, pendingTopupCents: 0, requiredCents: 10000, missingCents: 0 } }
 */
export function NegotiationShortfall({
  preview,
  onRecharge,
  atLeast = false,
}: NegotiationShortfallProps) {
  const shortfall = balanceShortfall(preview);
  const rechargeBRL = shortfall === "missing" ? ceilReais(preview.missingCents) : undefined;
  return (
    <div
      className="negotiationBalanceShortfall"
      role={shortfall ? "alert" : undefined}
      data-testid="negotiation-balance-shortfall"
    >
      {shortfall === "missing" && (
        <p data-testid="negotiation-balance-missing">
          Faltam {atLeast ? "pelo menos " : ""}
          {formatBRL(preview.missingCents)} para este lance. Disponível{" "}
          {formatBRL(preview.availableCents)}; {formatBRL(preview.reservedCents)} já reservados em
          outros lances da empresa.
        </p>
      )}
      {shortfall === "restricted" && <p>{RESTRICTED_COPY}</p>}
      {shortfall === "disabled" && <p>{DISABLED_COPY}</p>}
      {shortfall !== "disabled" && preview.reservedOnPostCents > 0 && (
        <p className="negotiationBalanceShortfallNote" data-testid="negotiation-balance-on-post">
          {formatBRL(preview.reservedOnPostCents)} estão reservados no lance anterior da empresa
          nesta publicação. Uma reserva superada é liberada em segundos, exceto o valor do lance que
          mantém o público menor de idade com o patrocínio habilitado.
        </p>
      )}
      <p className="negotiationBalanceShortfallNote">{RESERVATION_COPY}</p>
      {!onRecharge && (shortfall === "missing" || shortfall === "restricted") && (
        <p className="negotiationBalanceShortfallNote">
          Peça a alguém com permissão de Pagamentos para recarregar.
        </p>
      )}
      {onRecharge && (shortfall === "missing" || shortfall === "restricted") && (
        <button
          type="button"
          className="negotiationBalanceRechargeButton"
          onClick={() => onRecharge(rechargeBRL)}
        >
          Recarregar
        </button>
      )}
    </div>
  );
}
